import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { memo, useMemo } from 'react';

import type { SupporterRow } from '@/api/generated';
import { DataTable, type Column, type SortDirection } from '@/components/ui/DataTable';
import { testIdAttr, testIdPart } from '@/lib/testIds';

import { SupporterNameCell } from './SupporterNameCell';
import {
  DASH,
  formatDate,
  invitationStatusWord,
  notificationsWord,
  shownEmail,
  typeWord,
} from './supporterLabels';
import {
  PAGE_SIZES,
  SORT_COLUMN,
  type PageSize,
  type SupporterListType,
} from './useSupportersParams';

type SortKey = typeof SORT_COLUMN;

const EMPTY: Record<SupporterListType, string> = {
  followed: 'No one follows this organization yet.',
  invited: 'No invited members yet.',
};

interface SupportersTableProps {
  rows: SupporterRow[];
  type: SupporterListType;
  loading: boolean;
  /** True while the next view is on its way and this one is still shown. */
  refreshing: boolean;
  error: string | null;
  onRetry: () => void;
  direction: SortDirection;
  onSortChange: () => void;
  page: number;
  limit: PageSize;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: PageSize) => void;
  /** True when a search narrows the list. */
  filtered: boolean;
}

/** The wireframe's columns in its order (P3). They depend on nothing, so they are built once. */
const COLUMNS: Column<SupporterRow, SortKey>[] = [
  { key: 'name', header: 'Name', render: (row) => <SupporterNameCell row={row} /> },
  { key: 'email', header: 'Email', render: shownEmail },
  { key: 'type', header: 'Type', render: (row) => typeWord(row.type) },
  { key: 'invitation-status', header: 'Invitation Status', render: invitationStatusWord },
  { key: 'notifications', header: 'Notifications', render: notificationsWord },
  {
    key: 'supporter-since',
    header: 'Supporter Since',
    sortKey: SORT_COLUMN,
    render: (row) => formatDate(row.joined_at),
  },
  // No date until member invitations (US-052); no Actions column until then.
  { key: 'invitation-sent', header: 'Invitation Sent', render: () => DASH },
];

const rowKey = (row: SupporterRow) => row.id;

/**
 * The supporters table (PB2): one row per relationship, the fields the
 * backend's registry allows and nothing else (I1), sorted by Supporter Since
 * on the server, paged on the server (P14, NF1).
 *
 * Memoised, with its columns built once, so typing in the search box above it
 * rerenders the box and not a page of rows (scalability rule 4): the page
 * passes the settled search to the query, never the draft to this table.
 */
export const SupportersTable = memo(function SupportersTable({
  rows,
  type,
  loading,
  refreshing,
  error,
  onRetry,
  direction,
  onSortChange,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  filtered,
}: SupportersTableProps) {
  const sort = useMemo(() => ({ column: SORT_COLUMN, direction }), [direction]);

  return (
    <>
      <DataTable<SupporterRow, SortKey>
        caption="Supporters"
        testId="supporters-table"
        columns={COLUMNS}
        rows={rows}
        getRowKey={rowKey}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={onRetry}
        filtered={filtered}
        emptyMessage={EMPTY[type]}
        noMatchMessage="No one matches your search."
        sort={sort}
        onSortChange={onSortChange}
        page={page}
        pageSize={limit}
        total={total}
        onPageChange={onPageChange}
      />
      {!loading && !error && rows.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <TextField
            select
            size="small"
            label="Rows per page"
            value={String(limit)}
            onChange={(event) => onLimitChange(Number(event.target.value) as PageSize)}
            slotProps={{
              select: { SelectDisplayProps: testIdAttr('supporters-page-size-select') },
            }}
            fullWidth={false}
            sx={{ minWidth: 140 }}
          >
            {PAGE_SIZES.map((size) => (
              <MenuItem
                key={size}
                value={String(size)}
                data-testid={`supporters-page-size-option-${testIdPart(size)}`}
              >
                {size}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ) : null}
    </>
  );
});
